import React, { useEffect, useState, useRef, useCallback } from "react";
import HttpService from "../../services/httpService";
import { setSnackInfo } from "../../redux/actions/appAction";
import { connect } from "react-redux";
import { useSelector } from "react-redux";
import {
    TextField,
    Paper,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Tooltip,
    Chip,
    CircularProgress,
    LinearProgress,
    IconButton,
} from "@material-ui/core";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Skeleton from "@material-ui/lab/Skeleton";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import TuneIcon from "@material-ui/icons/Tune";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import RefreshIcon from "@material-ui/icons/Refresh";
import StorefrontIcon from "@material-ui/icons/Storefront";
import DateRangeOutlinedIcon from "@material-ui/icons/DateRangeOutlined";
import EventNoteIcon from "@material-ui/icons/EventNote";
import GetAppIcon from "@material-ui/icons/GetApp";
import CloseIcon from "@material-ui/icons/Close";
import InboxIcon from "@material-ui/icons/Inbox";
import PlaceOutlinedIcon from "@material-ui/icons/PlaceOutlined";
import { Button, Modal } from "antd";
import { CSVLink } from "react-csv";
import moment from "moment";
import { ROLENAME } from "../../utils/index";
import { Link } from "react-router-dom";

// Same palette as the Hold Revoke Calls dashboard - these two are the same
// subject seen from two angles, and they should read as one product.
const ORANGE = "#F47E44";
const ORANGE_DARK = "#c1531f";
const ORANGE_LIGHT = "#fff1ea";
const HEADER_GRADIENT = `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 60%, #ff9c66 100%)`;
const INK = "#3d2b21";
const NAVY = "#1C375D";

const BrandLinearProgress = withStyles(() => ({
    root: { height: 3, borderRadius: 999, backgroundColor: "rgba(193,83,31,0.10)" },
    bar: { borderRadius: 999, background: `linear-gradient(90deg, ${ORANGE_DARK}, ${ORANGE}, #ff9c66)` },
}))(LinearProgress);

const useStyles = makeStyles(() => ({
    root: { padding: "4px 4px 32px" },

    // ---- header -------------------------------------------------------------
    headerBanner: {
        display: "flex",
        alignItems: "center",
        gap: 16,
        background: HEADER_GRADIENT,
        borderRadius: 16,
        padding: "16px 24px",
        marginBottom: 14,
        color: "#fff",
        boxShadow: "0 8px 24px rgba(93,55,28,0.18)",
        flexWrap: "wrap",
    },
    headerIconWrap: {
        display: "grid",
        placeItems: "center",
        width: 46,
        height: 46,
        borderRadius: 14,
        flexShrink: 0,
        // Darkened rather than frosted: a translucent white panel loses almost
        // all of its contrast on the light end of the gradient.
        background: "rgba(0,0,0,0.18)",
    },
    headerTitle: { fontSize: 19, fontWeight: 800, lineHeight: 1.25 },
    headerSubtitle: { fontSize: 12.5, color: "rgba(255,255,255,0.9)", marginTop: 4, maxWidth: 680, lineHeight: 1.45 },
    headerChip: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 13px",
        borderRadius: 20,
        background: "rgba(0,0,0,0.26)",
        fontWeight: 700,
        fontSize: 12,
        whiteSpace: "nowrap",
        flexShrink: 0,
    },
    headerActions: { marginLeft: "auto", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 },
    "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
    "@keyframes pulseDot": {
        "0%, 100%": { opacity: 1, transform: "scale(1)" },
        "50%": { opacity: 0.4, transform: "scale(0.82)" },
    },
    refreshButton: {
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        padding: "8px 16px",
        borderRadius: 999,
        border: "none",
        background: "#fff",
        color: ORANGE_DARK,
        fontSize: 12.5,
        fontWeight: 800,
        fontFamily: "inherit",
        cursor: "pointer",
        whiteSpace: "nowrap",
        boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
        "&:hover": { background: "#fff6f1" },
        "&:disabled": { cursor: "default", opacity: 0.75 },
    },
    refreshSpinning: { animation: "$spin 0.9s linear infinite" },

    // ---- "something is happening" -------------------------------------------
    progressSlot: { height: 3, marginBottom: 0 },
    activity: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "9px 4px 0",
        minHeight: 26,
        fontSize: 11.5,
        fontWeight: 700,
        letterSpacing: 0.2,
        color: ORANGE_DARK,
    },
    activityDot: {
        width: 6,
        height: 6,
        borderRadius: "50%",
        flexShrink: 0,
        background: ORANGE_DARK,
        animation: "$pulseDot 1.2s ease-in-out infinite",
    },

    // ---- filters ------------------------------------------------------------
    filterPaper: {
        padding: "16px 20px",
        margin: "10px 0 14px",
        borderRadius: 16,
        border: "1px solid #eaeff6",
        boxShadow: "0 2px 16px rgba(28,55,93,0.06)",
    },
    filterHeadRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 8,
        marginBottom: 14,
    },
    filterHead: { display: "flex", alignItems: "center", gap: 8, color: ORANGE_DARK, fontWeight: 700, fontSize: 14 },
    filterBadge: {
        display: "flex",
        alignItems: "center",
        gap: 7,
        padding: "4px 12px",
        borderRadius: 999,
        background: ORANGE_LIGHT,
        color: ORANGE_DARK,
        fontSize: 12,
        fontWeight: 700,
    },
    filterRow: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12, transition: "opacity 0.2s ease" },
    field: {
        "& .MuiOutlinedInput-root": { borderRadius: 10 },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ORANGE },
        "& .MuiInputLabel-root.Mui-focused": { color: ORANGE_DARK },
    },
    pickerField: { width: 220, flexShrink: 0 },
    // The two dates are one range, so they sit in one bordered group with the
    // arrow between them rather than as two unrelated boxes.
    dateGroup: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "7px 12px 7px 14px",
        borderRadius: 12,
        border: "1px solid #dfe5ef",
        background: "#fff",
        flexShrink: 0,
        cursor: "pointer",
        transition: "border-color 150ms ease, box-shadow 150ms ease",
        "&:hover": { borderColor: "#c3ccd9" },
        "&:focus-within": { borderColor: ORANGE, boxShadow: `0 0 0 3px ${ORANGE}1f` },
    },
    dateGroupLabel: {
        fontSize: 10.5,
        fontWeight: 800,
        letterSpacing: 0.6,
        textTransform: "uppercase",
        color: "#9aa4b4",
        whiteSpace: "nowrap",
    },
    dateInput: {
        width: 124,
        "& input": { fontSize: 13, fontWeight: 600, color: INK, padding: "5px 0", cursor: "pointer" },
        "& .MuiInput-underline:before, & .MuiInput-underline:after": { display: "none" },
    },
    dateDash: { color: "#c3ccd9", fontSize: 13, fontWeight: 700, userSelect: "none" },
    filterActions: { display: "flex", alignItems: "center", gap: 8, marginLeft: "auto", flexShrink: 0 },
    "@media (max-width: 899.95px)": {
        filterActions: { marginLeft: 0, width: "100%", justifyContent: "flex-end" },
    },
    clearButton: {
        borderRadius: "20px !important",
        fontWeight: 500,
        display: "inline-flex !important",
        alignItems: "center !important",
    },
    applyButton: {
        borderRadius: "20px !important",
        fontWeight: 600,
        display: "inline-flex !important",
        alignItems: "center !important",
        color: "#fff !important",
        background: `linear-gradient(135deg, ${ORANGE_DARK}, ${ORANGE}) !important`,
        borderColor: "transparent !important",
        boxShadow: "0 4px 10px rgba(193,83,31,0.32) !important",
        // antd paints its own disabled grey, which the gradient above would
        // otherwise win against and leave the button looking enabled.
        "&.ant-btn-primary[disabled], &.ant-btn-primary[disabled]:hover": {
            background: "#efe9e5 !important",
            color: "#a89f98 !important",
            boxShadow: "none !important",
        },
    },
    halfRange: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 12px",
        borderRadius: 999,
        background: "#fff4e8",
        border: `1px solid ${ORANGE}66`,
        color: "#9c4a16",
        fontSize: 11.5,
        fontWeight: 700,
        whiteSpace: "nowrap",
    },
    filterHint: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        marginTop: 10,
        minHeight: 16,
        fontSize: 11.5,
        fontWeight: 600,
        color: "#8b7a6f",
    },

    // ---- stat tiles ---------------------------------------------------------
    tiles: { display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", marginBottom: 14 },
    tile: { borderRadius: 14, padding: "13px 16px", border: "1px solid #ece8e5", background: "#fbfaf9" },
    tileTop: { display: "flex", alignItems: "center", gap: 7, marginBottom: 6 },
    tileLabel: { fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.4 },
    tileValue: { fontSize: 25, fontWeight: 800, lineHeight: 1.05, color: INK },
    tileHint: { fontSize: 11, color: "#8b7a6f", marginTop: 4, lineHeight: 1.45 },

    // ---- pivot --------------------------------------------------------------
    card: {
        borderRadius: 16,
        border: "1px solid #eaeff6",
        boxShadow: "0 2px 16px rgba(28,55,93,0.06)",
        overflow: "hidden",
    },
    cardHead: {
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "14px 20px 12px",
        fontSize: 12.5,
        fontWeight: 800,
        color: ORANGE_DARK,
        textTransform: "uppercase",
        letterSpacing: 0.5,
    },
    cardNote: { marginLeft: "auto", fontSize: 11, fontWeight: 600, color: "#8b7a6f", textTransform: "none", letterSpacing: 0 },
    pivotWrap: { maxHeight: 520, overflow: "auto" },
    // Sticky in BOTH directions. The month column scrolls out of sight on a
    // wide pivot, and a grid of bare numbers with no row label is unreadable.
    headCell: {
        backgroundColor: ORANGE_DARK,
        // Pure white, not the warm #FFF2EB this used on navy: on #c1531f that
        // warm tint measures 4.23 and misses AA for normal text, while plain
        // white clears it at 4.64.
        color: "#fff",
        fontWeight: 700,
        fontSize: 12.5,
        letterSpacing: 0.3,
        borderBottom: "none",
        whiteSpace: "nowrap",
        textAlign: "center",
        padding: "10px 12px",
        position: "sticky",
        top: 0,
        zIndex: 3,
    },
    headCellCorner: { left: 0, zIndex: 5, textAlign: "left" },
    monthCell: {
        position: "sticky",
        left: 0,
        zIndex: 2,
        background: "#fff",
        fontWeight: 700,
        fontSize: 12.5,
        color: INK,
        whiteSpace: "nowrap",
        borderRight: "1px solid #eceff3",
    },
    valueCell: {
        textAlign: "center",
        fontSize: 13,
        padding: "8px 12px",
        fontWeight: 600,
        color: NAVY,
        cursor: "pointer",
        textDecoration: "underline",
        textUnderlineOffset: 2,
        "&:hover": { background: ORANGE_LIGHT, color: ORANGE_DARK },
    },
    // A zero opens an empty modal, so it is not a link. Muted and inert says
    // "nothing here" without inviting the click.
    zeroCell: {
        textAlign: "center",
        fontSize: 13,
        padding: "8px 12px",
        color: "#c3ccd9",
        cursor: "default",
        textDecoration: "none",
    },
    totalRow: { background: "#fbfaf9" },
    totalCell: { fontWeight: 800, color: "#1f5c3d" },
    emptyState: { textAlign: "center", padding: "46px 16px", color: "#8b7a6f" },
    emptyTitle: { fontSize: 14, fontWeight: 700, color: "#5f574f", marginTop: 8 },
    emptyHint: { fontSize: 12.5, color: "#8b7a6f", marginTop: 4 },

    // ---- modal --------------------------------------------------------------
    modalBar: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 14 },
    modalCount: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 12px",
        borderRadius: 999,
        background: ORANGE_LIGHT,
        color: ORANGE_DARK,
        fontSize: 12,
        fontWeight: 800,
    },
    exportBtn: {
        borderRadius: "20px !important",
        fontWeight: 600,
        display: "inline-flex !important",
        alignItems: "center !important",
        color: "#fff !important",
        background: "linear-gradient(135deg, #1f5c3d, #2e7d52) !important",
        borderColor: "transparent !important",
    },
    // Fills whatever the screen gives it instead of a fixed 440px, which on a
    // tall monitor showed ~10 rows with a cramped inner scroll and acres of
    // empty modal below it. 232px is the chrome around it: modal top/bottom
    // margin, the gradient header and the export bar.
    modalTableWrap: {
        maxHeight: "calc(100vh - 232px)",
        minHeight: 180,
        overflow: "auto",
        border: "1px solid #eceff3",
        borderRadius: 12,
    },
    modalHeadCell: {
        backgroundColor: ORANGE_DARK,
        // Pure white, not the warm #FFF2EB this used on navy: on #c1531f that
        // warm tint measures 4.23 and misses AA for normal text, while plain
        // white clears it at 4.64.
        color: "#fff",
        fontWeight: 700,
        fontSize: 12,
        whiteSpace: "nowrap",
        borderBottom: "none",
        position: "sticky",
        top: 0,
        zIndex: 2,
        padding: "9px 12px",
    },
    modalCell: { fontSize: 12.5, color: INK, whiteSpace: "nowrap", padding: "7px 12px" },
    modalRow: { "&:nth-of-type(even)": { background: "#fafbfd" }, "&:hover": { background: ORANGE_LIGHT } },
    leadLink: { color: NAVY, fontWeight: 700, textDecoration: "underline", textUnderlineOffset: 2 },
}));

const EMPTY_DATES = { startDate: "", endDate: "" };

const HoldDashboard = (props) => {
    const classes = useStyles();
    const { userDetails } = useSelector((state) => state.user);

    const [store, setStore] = useState([]);
    const [commonExpCenter, setExpCenter] = useState([]);
    const [commonLocation, setLocation] = useState([]);
    const [storeDateFilter, setPivotDatefilters] = useState(EMPTY_DATES);
    const [selectedExperienceCenter, setSelectedExperienceCenter] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState([]);

    const [showDetailedModal, setShowDetailedModal] = useState(false);
    const [popMonthData, setPopMonthData] = useState([]);
    const [csvData, setCsvData] = useState([]);
    const [tableCsvData, setTableCsvData] = useState([]);
    const csvButtonRef = useRef(null);
    const tableCsvButtonRef = useRef(null);

    // One loader per thing that can be in flight, instead of the single global
    // screen-blocking spinner. `loading` is the pivot itself and is what gates
    // the filters; the rest only grey out their own control.
    const [loading, setLoading] = useState(true);
    const [optionsLoading, setOptionsLoading] = useState(true);
    const [detailLoading, setDetailLoading] = useState(false);
    const [exporting, setExporting] = useState("");   // '' | 'full' | 'table'
    const [loadedOnce, setLoadedOnce] = useState(false);

    const startDateRef = useRef(null);
    const endDateRef = useRef(null);
    const openPicker = (ref) => {
        const input = ref.current;
        if (!input || input.disabled) return;
        input.focus();
        // showPicker landed in Chrome/Edge 99, is absent in Safari and throws
        // outside a user gesture - the focus above is the graceful fallback.
        try {
            if (typeof input.showPicker === "function") input.showPicker();
        } catch (e) { /* focus already happened */ }
    };

    const headers = [
        { key: "serialNo", label: "Sl. No." },
        { key: "projectCode", label: "Project No." },
        { key: "leadNumber", label: "Lead No." },
        { key: "createdAt", label: "Created At" },
        { key: "leadAssignedDate", label: "Lead Assigned Date" },
        { key: "meetingDate", label: "Meeting Date" },
        { key: "salesWonDate", label: "Sales Won Date" },
        { key: "customerDesignSignOffDate", label: "Design Won Date" },
        { key: "actualCompletionDate", label: "Completed Date" },
        { key: "sourceOfLead", label: "Source Of Lead" },
        { key: "pageURL", label: "Page URL" },
        { key: "clientName", label: "Client Name" },
        { key: "location", label: "City" },
        { key: "expCtrName", label: "Experience Center" },
        { key: "clientAddress", label: "Address" },
        { key: "currentStage", label: "Current Stage" },
        { key: "leadType", label: "Lead Type" },
        { key: "assignTo", label: "Assigned To" },
        { key: "department", label: "Department" },
        { key: "lostPostMeeting", label: "Lost Post Meeting" },
        { key: "tokenPercent", label: "Token % Received" },
        { key: "reqNotCollectedMoveDate", label: "Requirements Not Collected Movement Date" },
        { key: "moveStageRnc", label: "Reason for lead to be in Requirements Not Collected Stage" },
        { key: "verificationStatus", label: "Lead verification status" },
        { key: "reqCollectionDate", label: "Requirement Collection Date" },
        { key: "reqCollectedMoveDate", label: "Requirements Collected Stage Movement Date" },
        { key: "reasonStayInRc", label: "Reason for lead to stay in Requirements Collected Stage" },
        { key: "detailsScope ", label: "Scope of work in detail" },
        { key: "designPreferences ", label: "Design Preferences Details" },
        { key: "detailsBudget", label: "Budget Details" },
        { key: "detailsTimeline", label: "Timeline to move into the house" },
        { key: "possessionDate", label: "Possession Date" },
        { key: "attachFloorPlan", label: "Attach Floor Plan link" },
        { key: "anyOtherSpecificDetails", label: "Any other specific details relevant to Decorpot" },
        { key: "QtSentMoveDate", label: "Quotation Sent Movement Date" },
        { key: "reasonDealQt", label: "Reason for deal to be in Quotation Sent stage" },
        { key: "cxFittingBudget", label: "Is Quotation fitting customer budget mentioned" },
        { key: "planVisitExpZone", label: "Is customer planning to visit the Experience Zone" },
        { key: "cxMoveTimeline", label: "Cx Move-in Timeline" },
        { key: "possessionStatus", label: "Possession Status" },
        { key: "registrationStatus", label: "Registration Status" },
        { key: "decisionMaker", label: "Decision Maker" },
        { key: "financialReadiness", label: "Financial Readiness" },
        { key: "budgetQuotation", label: "Budget Vs Quotation" },
        { key: "competitorInvolvement", label: "Competitors Involvement" },
        { key: "otherCompetitorValue", label: "Other Competitor" },
        { key: "expectedSalesWonDate", label: "Expected Sales Won Date" },
        { key: "proposeMeetingType", label: "Propose Meeting Type" },
        { key: "proposeMeetingDate", label: "Propose Meeting Date" },
        { key: "proposeMeetingParticipant", label: "Meeting Participant" },
        { key: "salesUser", label: "Sales Executive" },
        { key: "salesASM", label: "Sales ASM" },
        { key: "salesManager", label: "Sales Manager" },
        { key: "salesAGM", label: "Sales AGM" },
        { key: "salesDGM", label: "Sales DGM" },
        { key: "prevStage", label: "Stage before Lost" },
    ];

    const tableHeaders = [
        { key: "serialNo", label: "Sl. No." },
        { key: "projectCode", label: "Project No." },
        { key: "leadNumber", label: "Lead No." },
        { key: "createdAt", label: "Created At" },
        { key: "holdRevokeDate", label: "Hold Revoke Date" },
        { key: "sourceOfLead", label: "Source of Lead" },
        { key: "clientName", label: "Client Name" },
        { key: "assignTo", label: "Assigned To" },
        { key: "address", label: "Address" },
        { key: "city", label: "City" },
        { key: "expCtrName", label: "Exp. Ctr. Name" },
        { key: "currentStage", label: "Current Stage" },
        { key: "leadType", label: "Lead Type" },
    ];

    const csvDownloadHandler = async () => {
        setExporting("full");
        try {
            let csvArray = [];
            for (let i = 0; i < popMonthData.length; i++) {
                let obj = {
                    serialNo: i + 1,
                    projectCode: popMonthData[i]?.erpProjectNo || "N/A",
                    leadNumber: popMonthData[i]?.lead_no || "N/A",
                    meetingDate: popMonthData[i]?.meetingDate ? moment(popMonthData[i]?.meetingDate).format("DD-MM-YYYY") : "-",
                    createdAt: popMonthData[i]?.createdAt ? moment(popMonthData[i]?.createdAt).format("DD-MM-YYYY") : "-",
                    leadAssignedDate: popMonthData[i]?.leadAssignedDate ? moment(popMonthData[i]?.leadAssignedDate).format("DD-MM-YYYY") : "-",
                    sourceOfLead: popMonthData[i]?.sourceOfLead?.name || "N/A",
                    pageURL: popMonthData[i]?.pageURL ? popMonthData[i]?.pageURL : "-",
                    clientName: popMonthData[i]?.customerId?.name,
                    clientAddress: popMonthData[i]?.customerId?.address,
                    totalValue: popMonthData[i].staticSalesWonBudget,
                    location: popMonthData[i]?.experienceCenterId?.locationId?.name,
                    expCtrName: popMonthData[i]?.experienceCenterId?.name,
                    salesWonDate: popMonthData[i]?.leadWonDate ? moment(popMonthData[i]?.leadWonDate).format("DD-MM-YYYY") : "-",
                    currentStage: popMonthData[i].currentStage ? popMonthData[i]?.currentStage : "NA",
                    leadType: popMonthData[i].leadType ? popMonthData[i]?.leadType : "NA",
                    assignTo: popMonthData[i].assignTo?.name ? popMonthData[i]?.assignTo?.name : "NA",
                    department: popMonthData[i]?.departmentId?.name ? popMonthData[i]?.departmentId?.name : "NA",
                    designStage: popMonthData[i]?.stage ? popMonthData[i]?.stage : "NA",
                    internalDesignSignOffDate: popMonthData[i]?.internalDesignSignOffDate ? moment(popMonthData[i]?.internalDesignSignOffDate).format("DD-MM-YYYY") : "-",
                    customerDesignSignOffDate: popMonthData[i]?.customerDesignSignOffDate ? moment(popMonthData[i]?.customerDesignSignOffDate).format("DD-MM-YYYY") : "-",
                    designSignOffDate: popMonthData[i]?.customerDesignSignOffDate ? moment(popMonthData[i]?.customerDesignSignOffDate).format("DD-MM-YYYY") : "-",
                    materialDeliveryDate: popMonthData[i]?.materialDispatchedDate ? moment(popMonthData[i]?.materialDispatchedDate).format("DD-MM-YYYY") : "-",
                    actualCompletionDate: popMonthData[i]?.executionCompletionDate ? moment(popMonthData[i]?.executionCompletionDate).format("DD-MM-YYYY") : "-",
                    lostPostMeeting: popMonthData[i]?.isLost?.length > 0
                        ? popMonthData[i].isLost[0]?.isLostPostMeeting === true
                            ? "Yes"
                            : popMonthData[i].isLost[0]?.isLostPostMeeting === false
                                ? "No"
                                : "NA"
                        : "NA",
                    tokenPercent: popMonthData[i]?.tokenPercent !== null ? popMonthData[i]?.tokenPercent : "null",
                    reqNotCollectedMoveDate: popMonthData[i]?.salesStageData?.reqNotCollectedData?.moveDateInRnc ? moment(popMonthData[i]?.salesStageData?.reqNotCollectedData?.moveDateInRnc).format("DD-MM-YYYY") : "-",
                    moveStageRnc: popMonthData[i]?.salesStageData?.reqNotCollectedData?.reasonToMove ? popMonthData[i]?.salesStageData?.reqNotCollectedData?.reasonToMove : "-",
                    verificationStatus: popMonthData[i]?.salesStageData?.reqNotCollectedData?.verifyStatus ? popMonthData[i]?.salesStageData?.reqNotCollectedData?.verifyStatus : "NA",
                    reqCollectionDate: popMonthData[i]?.salesStageData?.reqCollectedData?.reqCollectionDate ? moment(popMonthData[i]?.salesStageData?.reqCollectedData?.reqCollectionDate).format("DD-MM-YYYY") : "-",
                    reqCollectedMoveDate: popMonthData[i]?.salesStageData?.reqCollectedData?.moveDateInReqCol ? moment(popMonthData[i]?.salesStageData?.reqCollectedData?.moveDateInReqCol).format("DD-MM-YYYY") : "-",
                    reasonStayInRc: popMonthData[i]?.salesStageData?.reqCollectedData?.reasonMoveRcCollect || "-",
                    detailsScope: popMonthData[i]?.salesStageData?.reqCollectedData?.rcScopeOfWork || "-",
                    designPreferences: popMonthData[i]?.salesStageData?.reqCollectedData?.designPref || "-",
                    detailsBudget: popMonthData[i]?.salesStageData?.reqCollectedData?.estimatedBudget || "-",
                    detailsTimeline: popMonthData[i]?.salesStageData?.reqCollectedData?.timelineMoveHouseDate ? moment(popMonthData[i]?.salesStageData?.reqCollectedData?.timelineMoveHouseDate).format("DD-MM-YYYY") : "-",
                    possessionDate: popMonthData[i]?.salesStageData?.reqCollectedData?.possDate ? moment(popMonthData[i]?.salesStageData?.reqCollectedData?.possDate).format("DD-MM-YYYY") : "-",
                    attachFloorPlan: popMonthData[i]?.salesStageData?.reqCollectedData?.floorPlanAttach || "-",
                    anyOtherSpecificDetails: popMonthData[i]?.salesStageData?.reqCollectedData?.rcOtherDetails || "-",
                    QtSentMoveDate: popMonthData[i]?.salesStageData?.qtSentData?.moveDateQtSent ? moment(popMonthData[i]?.salesStageData?.qtSentData?.moveDateQtSent).format("DD-MM-YYYY") : "-",
                    reasonDealQt: popMonthData[i]?.salesStageData?.qtSentData?.reasonForDealQt || "-",
                    cxFittingBudget: popMonthData[i]?.salesStageData?.qtSentData?.qtFitBudget || "-",
                    planVisitExpZone: popMonthData[i]?.salesStageData?.qtSentData?.cxPlanDateVisitStatus || "-",
                    cxMoveTimeline: popMonthData[i]?.hotDealChecklistData?.cxMoveTimeline || "NA",
                    possessionStatus: popMonthData[i]?.hotDealChecklistData?.possessionStatus || "NA",
                    registrationStatus: popMonthData[i]?.hotDealChecklistData?.registrationStatus || "NA",
                    decisionMaker: popMonthData[i]?.hotDealChecklistData?.decisionMaker || "NA",
                    financialReadiness: popMonthData[i]?.hotDealChecklistData?.financialReadiness || "NA",
                    budgetQuotation: popMonthData[i]?.hotDealChecklistData?.budgetQuotation || "NA",
                    competitorInvolvement: popMonthData[i]?.hotDealChecklistData?.competitorInvolvement || "NA",
                    otherCompetitorValue: popMonthData[i]?.hotDealChecklistData?.otherCompetitorValue || "NA",
                    expectedSalesWonDate: popMonthData[i]?.hotDealChecklistData?.expectedSalesWonDate ? moment(popMonthData[i]?.hotDealChecklistData?.expectedSalesWonDate).format("DD-MM-YYYY") : "NA",
                    proposeMeetingType: popMonthData[i]?.salesStageData?.qtSentMeetingDetail?.proposeMeetingType || "NA",
                    proposeMeetingDate: popMonthData[i]?.salesStageData?.qtSentMeetingDetail?.proposeMeetingDate ? moment(popMonthData[i]?.salesStageData?.qtSentMeetingDetail?.proposeMeetingDate).format("DD-MM-YYYY") : "NA",
                    proposeMeetingParticipant: popMonthData[i]?.salesStageData?.qtSentMeetingDetail?.meetingParticipant || "NA",
                    salesUser: popMonthData[i].salesUser?.name ? popMonthData[i]?.salesUser?.name : "NA",
                    salesASM: popMonthData[i].salesAssistantManager?.name ? popMonthData[i]?.salesAssistantManager?.name : "NA",
                    salesManager: popMonthData[i].salesManager?.name ? popMonthData[i]?.salesManager?.name : "NA",
                    salesAGM: popMonthData[i].salesAGM?.name ? popMonthData[i]?.salesAGM?.name : "NA",
                    salesDGM: popMonthData[i].salesDGM?.name ? popMonthData[i]?.salesDGM?.name : "NA",
                    prevStage: popMonthData[i].prevStage ? popMonthData[i]?.prevStage : "Not Lost",
                };
                csvArray.push(obj);
            }
            setCsvData(csvArray);
            // The hidden CSVLink has to re-render with the new rows before its
            // anchor is clicked, which is what this defers past.
            setTimeout(() => csvButtonRef.current && csvButtonRef.current.link.click(), 100);
        } catch (error) {
            console.log("error", error);
            props?.setSnackInfo(error?.response?.data?.message ?? "Something went wrong", "error");
        }
        setExporting("");
    };

    const generateTableCsv = () => {
        setExporting("table");
        try {
            let csvArray = [];
            if (popMonthData && popMonthData.length > 0) {
                popMonthData.forEach((data, i) => {
                    let obj = {
                        serialNo: i + 1,
                        projectCode: data?.erpProjectNo || "NA",
                        leadNumber: data?.lead_no || "NA",
                        createdAt: data?.createdAt ? moment(data?.createdAt).format("DD-MM-YYYY") : "-",
                        // Prefer the new field, fall back to the old one while
                        // the rename is in flight. (An older version read
                        // data.handoverData - a typo - so this column was "-"
                        // on every row.)
                        holdRevokeDate: (data?.holdRevokeDate || data?.handoverDate)
                            ? moment(data?.holdRevokeDate || data?.handoverDate).format("DD-MM-YYYY") : "-",
                        sourceOfLead: data?.sourceOfLead?.name || "N/A",
                        clientName: data?.customerId?.name || "N/A",
                        assignTo: data?.assignTo?.name || "N/A",
                        address: data?.customerId?.address || "N/A",
                        city: data?.experienceCenterId?.locationId?.name || "N/A",
                        expCtrName: data?.experienceCenterId?.name || "N/A",
                        currentStage: data?.currentStage ? data.currentStage : "NA",
                        leadType: data?.leadType ? data.leadType : "NA",
                    };
                    csvArray.push(obj);
                });
            }
            setTableCsvData(csvArray);
            setTimeout(() => tableCsvButtonRef.current && tableCsvButtonRef.current.link.click(), 100);
        } catch (error) {
            console.error("Error generating table CSV:", error);
            props?.setSnackInfo("Error generating table CSV", "error");
        }
        setExporting("");
    };

    // ---- pivot shape --------------------------------------------------------
    // Unchanged from before: `store` is one row per experience centre, each
    // carrying its own monthlyGrandTotal series.
    const expCenterData = {};
    store.forEach((el) => {
        if (!expCenterData[el.experienceCenterName]) expCenterData[el.experienceCenterName] = [];
        expCenterData[el.experienceCenterName].push(el);
    });
    const uniqueexperienceCenterCode = Array.from(new Set(store.map((el) => el.expCenterCode)));
    const uniqueexperienceCenterNames = Array.from(new Set(store.map((el) => el.experienceCenterName)));
    const months = store[0]?.monthlyGrandTotal || [];
    // The LAST row of `store` is a roll-up the server appends
    // (experienceCenterName: "Grand Total", count already summed across every
    // centre) - see experienceCenterTotalsWithGrandTotal in
    // automationLead.js. Summing every row therefore counts the whole
    // dashboard twice, which is why this tile read double the pivot's own
    // Grand Total. Use the roll-up when it is there; sum the real centres
    // only if it is not.
    const totalRow = store.find((el) => el.experienceCenterName === "Grand Total");
    const grandTotal = totalRow
        ? (totalRow.count || 0)
        : store.reduce((sum, el) => sum + (el.count || 0), 0);
    // The roll-up is a column in the pivot, not a centre - excluded so the
    // "Experience centres" tile matches what is actually on screen.
    const realCentreCount = uniqueexperienceCenterNames.filter((n) => n !== "Grand Total").length;

    // ---- dependent filters --------------------------------------------------
    // The experience-centre list narrows to the chosen locations. Done here
    // rather than by refetching: /experience-center already populates
    // locationId and the result is cached in localStorage, so the dependency
    // costs nothing and cannot fall out of step with the list on screen.
    // Filtering the user's OWN centres also means it can never offer one they
    // are not scoped to, which a fetch-by-city would.
    const locationIdOf = (ec) => String((ec && ec.locationId && ec.locationId._id) || (ec && ec.locationId) || "");
    const visibleExpCenters = selectedLocation.length
        ? commonExpCenter.filter((ec) => selectedLocation.some((loc) => String(loc._id) === locationIdOf(ec)))
        : commonExpCenter;

    // ---- data ---------------------------------------------------------------
    const expCenters = async () => {
        try {
            let response = await HttpService.getExpericenCenters();
            const matchedUsers = response.data.filter((user) => userDetails.experienceCenterId.includes(user._id));
            setExpCenter(matchedUsers);
        } catch (error) {
            console.error("Error fetching experience centers:", error);
            props.setSnackInfo(error?.response?.data ?? "Something Went Wrong", "error");
        }
    };

    const locations = async () => {
        try {
            let response = await HttpService.getLocation();
            const matchedUsers = response.data.filter((user) => userDetails.selectedLocation.includes(user._id));
            setLocation(matchedUsers);
        } catch (error) {
            console.error("Error fetching locations:", error);
            props.setSnackInfo(error?.response?.data ?? "Something Went Wrong", "error");
        }
    };

    // `dates` lets a caller fetch with values state has not committed yet -
    // Clear resets and refetches in the same tick, and the closure here would
    // otherwise still hold the pre-Clear range.
    const holdDashboard = async (addLocation, addExpCenter, dates) => {
        const range = dates || storeDateFilter;
        let locationData = (addLocation || selectedLocation)?.map((el) => el._id);
        let expCenterData_ = (addExpCenter || selectedExperienceCenter)?.map((el) => el._id);
        let obj = {
            startDate: range.startDate,
            endDate: range.endDate,
            selectedLocation: locationData ? locationData : "",
            selectedExperienceCenter: expCenterData_ ? expCenterData_ : "",
            userLocation: userDetails.selectedLocation,
            userExpCenter: userDetails.selectedExpCenter,
            // There is no Source of Lead picker on this page, and the state
            // behind it had no setter - it was permanently [], which the query
            // string rendered as "". Sent as "" so the wire is unchanged.
            selectedSourceOfLead: "",
        };
        setLoading(true);
        try {
            let response = await HttpService.getHoldLeads(obj);
            setStore(response.data);
        } catch (error) {
            console.log(error, "err");
            props.setSnackInfo(error?.response?.data ?? "Something Went Wrong", "error");
        }
        setLoading(false);
        setLoadedOnce(true);
    };

    const bootstrap = useCallback(async () => {
        setOptionsLoading(true);
        await Promise.all([expCenters(), locations()]);
        setOptionsLoading(false);
        holdDashboard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        bootstrap();
    }, [bootstrap]);

    const storePivotDate = (e) => {
        const { name, value } = e.target;
        setPivotDatefilters({ ...storeDateFilter, [name]: value });
    };

    // The dates are applied by the button, not by typing - the two pickers
    // apply on pick, the range waits for Apply.
    //
    // Both ends, or neither. /holdDashboard branches on `!startDate &&
    // !endDate` and on `startDate && endDate` and has NO branch for one of the
    // two, which leaves firstDay/lastDay undefined and the aggregation
    // comparing against nothing - so Apply stays disabled until the range is
    // whole.
    const dateRangeReady = Boolean(storeDateFilter.startDate) === Boolean(storeDateFilter.endDate);

    const handleMultipleLocationData = (e, newvalue) => {
        setSelectedLocation(newvalue);
        // Drop any centre that no longer sits in the chosen locations. Keeping
        // it would leave a selection the list no longer offers, silently
        // scoping the pivot to a centre the user can neither see nor remove.
        const keptCentres = newvalue.length
            ? selectedExperienceCenter.filter((ec) =>
                newvalue.some((loc) => String(loc._id) === locationIdOf(ec)))
            : selectedExperienceCenter;
        if (keptCentres.length !== selectedExperienceCenter.length) {
            setSelectedExperienceCenter(keptCentres);
        }
        // Passed explicitly: the state set above has not committed yet, so the
        // closure here still holds the pre-change values.
        holdDashboard(newvalue, keptCentres);
    };

    const handleMultipleExpData = (e, newvalue) => {
        setSelectedExperienceCenter(newvalue);
        holdDashboard(selectedLocation, newvalue);
    };

    const clearFilters = () => {
        setSelectedLocation([]);
        setSelectedExperienceCenter([]);
        setPivotDatefilters(EMPTY_DATES);
        // Clear is an action, not an edit - it means "show me everything", so
        // it applies itself rather than asking for a second click. The empties
        // are passed explicitly because this closure still holds the old ones.
        holdDashboard([], [], EMPTY_DATES);
    };

    const getDataBasedOnClickHold = async (e, month, innerEl, val) => {
        let obj = {};
        let expCenterIds =
            selectedExperienceCenter && selectedExperienceCenter.length > 0
                ? selectedExperienceCenter.map((el) => el._id)
                : "";
        let locationData =
            selectedLocation && selectedLocation.length > 0 ? selectedLocation.map((el) => el._id) : "";
        if (val === "leads") {
            obj = {
                _id: { month: month.monthNumber, year: month.year },
                dateInfo: storeDateFilter,
                selectedLocation: innerEl.locationId ? innerEl.locationId : locationData,
                selectedExperienceCenter: innerEl.experienceCenterId ? innerEl.experienceCenterId : expCenterIds,
                userLocation: userDetails.selectedLocation,
                userExpCenter: userDetails.experienceCenterId,
                selectedSourceOfLead: "",
            };
        } else if (val === "total") {
            if (innerEl === "Grand Total") {
                obj = {
                    _id: { startMonth: month[0].monthNumber, startYear: month[0].year, endMonth: month[month.length - 1].monthNumber, endYear: month[month.length - 1].year },
                    dateInfo: storeDateFilter,
                    selectedLocation: locationData,
                    selectedExperienceCenter: expCenterIds,
                    userExpCenter: userDetails.experienceCenterId,
                    userLocation: userDetails.selectedLocation,
                    selectedSourceOfLead: "",
                };
            } else {
                let expCenter;
                for (let i = 0; i < store.length; i++) {
                    if (store[i].experienceCenterName === innerEl) {
                        expCenter = store[i].experienceCenterId;
                        break;
                    }
                }
                obj = {
                    _id: { startMonth: month[0].monthNumber, startYear: month[0].year, endMonth: month[month.length - 1].monthNumber, endYear: month[month.length - 1].year },
                    dateInfo: storeDateFilter,
                    selectedLocation: locationData,
                    selectedExperienceCenter: expCenter,
                    userExpCenter: userDetails.experienceCenterId,
                    userLocation: userDetails.selectedLocation,
                    selectedSourceOfLead: "",
                };
            }
        }
        // Opened first, with its own spinner inside, so the click gets an
        // immediate answer instead of the page seeming to ignore it.
        setPopMonthData([]);
        setShowDetailedModal(true);
        setDetailLoading(true);
        try {
            let response = await HttpService.holdDashboardData(obj);
            setPopMonthData(response.data);
        } catch (error) {
            console.log("Error ->", error);
            props.setSnackInfo(error?.response?.data ?? "Something Went Wrong", "error");
            setShowDetailedModal(false);
        }
        setDetailLoading(false);
    };

    const isMarketingAnalyst = userDetails.roles.some((user) => user.name === ROLENAME.marketingAnalyst);

    const filtersBusy = loading || optionsLoading;
    const activeFilterCount = selectedLocation.length + selectedExperienceCenter.length
        + (storeDateFilter.startDate ? 1 : 0) + (storeDateFilter.endDate ? 1 : 0);

    const rangeLabel = storeDateFilter.startDate && storeDateFilter.endDate
        ? `${moment(storeDateFilter.startDate).format("DD MMM YY")} — ${moment(storeDateFilter.endDate).format("DD MMM YY")}`
        : "All hold revoke dates";

    const activity = loading
        ? "Fetching hold leads across the selected centres…"
        : optionsLoading
            ? "Loading the location and experience-centre lists…"
            : detailLoading
                ? "Opening the leads behind that number…"
                : "";

    const cellValue = (innerEl, month) => Number(
        innerEl.monthlyGrandTotal.find((entry) => entry.monthName === month.monthName && entry.year === month.year)?.count || 0,
    );

    return (
        <div className={classes.root}>
            {/* ------------------------------------------------------- header */}
            <div className={classes.headerBanner}>
                <div className={classes.headerIconWrap}>
                    <PauseCircleOutlineIcon style={{ fontSize: 26, color: "#fff" }} />
                </div>
                <div style={{ minWidth: 0 }}>
                    <div className={classes.headerTitle}>Hold Leads</div>
                    <div className={classes.headerSubtitle}>
                        Leads parked on Hold, counted by the month the customer asked to be approached
                        again and split across experience centres. Click any number to see the leads behind it.
                    </div>
                </div>
                <div className={classes.headerChip}>
                    <DateRangeOutlinedIcon style={{ fontSize: 15 }} />
                    {rangeLabel}
                </div>
                <div className={classes.headerActions}>
                    <Tooltip title={filtersBusy ? "Already fetching…" : "Fetch the latest hold leads"}>
                        {/* The span is required: a disabled button fires no
                            mouse events, so the tooltip would die mid-refresh. */}
                        <span style={{ display: "inline-flex" }}>
                            <button
                                type="button"
                                className={classes.refreshButton}
                                onClick={() => holdDashboard()}
                                disabled={filtersBusy}
                            >
                                <RefreshIcon className={loading ? classes.refreshSpinning : undefined} style={{ fontSize: 17 }} />
                                {loading ? "Refreshing…" : "Refresh"}
                            </button>
                        </span>
                    </Tooltip>
                </div>
            </div>

            {/* The bar is driven by ANY in-flight request, and the caption says
                which - a bar on its own only says "wait". */}
            <div className={classes.progressSlot}>{activity ? <BrandLinearProgress /> : null}</div>
            <div className={classes.activity}>
                {activity && (
                    <>
                        <span className={classes.activityDot} />
                        {activity}
                    </>
                )}
            </div>

            {/* ------------------------------------------------------ filters */}
            <Paper elevation={0} className={classes.filterPaper}>
                <div className={classes.filterHeadRow}>
                    <div className={classes.filterHead}>
                        <TuneIcon fontSize="small" />
                        Filters
                        {activeFilterCount > 0 && (
                            <Chip
                                size="small"
                                label={`${activeFilterCount} active`}
                                style={{ background: ORANGE_LIGHT, color: ORANGE_DARK, fontWeight: 700, height: 20 }}
                            />
                        )}
                    </div>
                    {filtersBusy && (
                        <div className={classes.filterBadge}>
                            <CircularProgress size={12} thickness={5} style={{ color: ORANGE_DARK }} />
                            Fetching data, please wait…
                        </div>
                    )}
                </div>

                {/* Everything here is locked while a request is in flight. A
                    filter changed mid-fetch would race the response and could
                    leave the pivot showing a scope the controls do not. */}
                <div
                    className={classes.filterRow}
                    style={{ opacity: filtersBusy ? 0.55 : 1, pointerEvents: filtersBusy ? "none" : "auto" }}
                >
                    <Autocomplete
                        multiple
                        size="small"
                        limitTags={1}
                        disabled={filtersBusy}
                        value={selectedLocation}
                        onChange={handleMultipleLocationData}
                        options={commonLocation}
                        getOptionLabel={(option) => option.name || ""}
                        getOptionSelected={(option, value) => option._id === value._id}
                        className={`${classes.field} ${classes.pickerField}`}
                        noOptionsText="No location you can see"
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Location"
                                variant="outlined"
                                placeholder={selectedLocation.length ? "" : "All locations"}
                            />
                        )}
                    />
                    <Autocomplete
                        multiple
                        size="small"
                        limitTags={1}
                        disabled={filtersBusy}
                        value={selectedExperienceCenter}
                        onChange={handleMultipleExpData}
                        options={visibleExpCenters}
                        getOptionLabel={(option) => option.name || ""}
                        getOptionSelected={(option, value) => option._id === value._id}
                        className={`${classes.field} ${classes.pickerField}`}
                        noOptionsText={selectedLocation.length
                            ? `No experience centre you can see in ${selectedLocation.map((l) => l.name).join(", ")}`
                            : "No experience centre you can see"}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Experience Center"
                                variant="outlined"
                                placeholder={selectedExperienceCenter.length
                                    ? ""
                                    : selectedLocation.length
                                        ? `All ${visibleExpCenters.length} in this location`
                                        : "All centres"}
                            />
                        )}
                    />

                    {/* One range, so one control. Clicking the label, the arrow
                        or the padding opens the start picker; clicking either
                        input opens that one's own. */}
                    <div
                        className={classes.dateGroup}
                        onClick={(event) => {
                            if (event.target.tagName !== "INPUT") openPicker(startDateRef);
                        }}
                    >
                        <span className={classes.dateGroupLabel}>Hold Revoke</span>
                        <TextField
                            id="startDate"
                            name="startDate"
                            type="date"
                            size="small"
                            className={classes.dateInput}
                            inputRef={startDateRef}
                            inputProps={{ max: storeDateFilter.endDate || undefined, "aria-label": "Hold revoke start date" }}
                            value={storeDateFilter.startDate}
                            onClick={() => openPicker(startDateRef)}
                            onChange={storePivotDate}
                            disabled={filtersBusy}
                        />
                        <span className={classes.dateDash}>→</span>
                        <TextField
                            id="endDate"
                            name="endDate"
                            type="date"
                            size="small"
                            className={classes.dateInput}
                            inputRef={endDateRef}
                            inputProps={{ min: storeDateFilter.startDate || undefined, "aria-label": "Hold revoke end date" }}
                            value={storeDateFilter.endDate}
                            onClick={() => openPicker(endDateRef)}
                            onChange={storePivotDate}
                            disabled={filtersBusy}
                        />
                    </div>

                    <div className={classes.filterActions}>
                        {/* Says WHY Apply is greyed out. A disabled button with
                            no reason beside it is the thing people file as a
                            bug. */}
                        {!dateRangeReady && !filtersBusy && (
                            <span className={classes.halfRange}>
                                <EventNoteIcon style={{ fontSize: 14 }} />
                                Pick the other end of the range
                            </span>
                        )}
                        <Button
                            type="primary"
                            icon={<SearchIcon style={{ fontSize: 16, marginRight: 6 }} />}
                            onClick={() => holdDashboard()}
                            className={classes.applyButton}
                            loading={loading}
                            disabled={filtersBusy || !dateRangeReady}
                        >
                            {loading ? "Loading..." : "Apply dates"}
                        </Button>
                        <Button
                            icon={<ClearIcon style={{ fontSize: 16, marginRight: 6 }} />}
                            onClick={clearFilters}
                            className={classes.clearButton}
                            disabled={filtersBusy || activeFilterCount === 0}
                        >
                            Clear
                        </Button>
                    </div>
                </div>

                {/* The dependency is only worth saying out loud once it has
                    actually narrowed something - otherwise it is noise. */}
                <div className={classes.filterHint}>
                    {selectedLocation.length > 0 ? (
                        <>
                            <PlaceOutlinedIcon style={{ fontSize: 13 }} />
                            {`Showing the ${visibleExpCenters.length} experience centre${visibleExpCenters.length === 1 ? "" : "s"} in ${selectedLocation.map((l) => l.name).join(", ")}`}
                            {selectedExperienceCenter.length > 0 && ` · ${selectedExperienceCenter.length} selected`}

                        </>
                    ) : (
                        <>
                            <SearchIcon style={{ fontSize: 13 }} />
                            Pick a Location to narrow the Experience Center list. Location and Experience
                            Center apply as soon as you pick them; the hold revoke dates
                            need <b style={{ margin: "0 3px" }}>Apply dates</b>.
                        </>
                    )}
                </div>
            </Paper>

            {/* -------------------------------------------------- stat tiles */}
            <div className={classes.tiles}>
                <div className={classes.tile} style={{ background: ORANGE_LIGHT, borderColor: `${ORANGE_DARK}22` }}>
                    <div className={classes.tileTop}>
                        <PauseCircleOutlineIcon style={{ fontSize: 17, color: ORANGE_DARK }} />
                        <span className={classes.tileLabel} style={{ color: ORANGE_DARK }}>Total on hold</span>
                    </div>
                    <div className={classes.tileValue}>
                        {loading ? <Skeleton variant="text" width={56} /> : grandTotal.toLocaleString()}
                    </div>
                    <div className={classes.tileHint}>Across every centre and month shown</div>
                </div>
                <div className={classes.tile}>
                    <div className={classes.tileTop}>
                        <StorefrontIcon style={{ fontSize: 17, color: NAVY }} />
                        <span className={classes.tileLabel} style={{ color: NAVY }}>Experience centres</span>
                    </div>
                    <div className={classes.tileValue}>
                        {loading ? <Skeleton variant="text" width={40} /> : realCentreCount}
                    </div>
                    <div className={classes.tileHint}>Columns in the pivot, excluding Grand Total</div>
                </div>
                <div className={classes.tile}>
                    <div className={classes.tileTop}>
                        <EventNoteIcon style={{ fontSize: 17, color: "#1f5c3d" }} />
                        <span className={classes.tileLabel} style={{ color: "#1f5c3d" }}>Hold revoke months</span>
                    </div>
                    <div className={classes.tileValue}>
                        {loading ? <Skeleton variant="text" width={40} /> : months.length}
                    </div>
                    <div className={classes.tileHint}>Rows in the pivot below</div>
                </div>
            </div>

            {/* ------------------------------------------------------- pivot */}
            <Paper elevation={0} className={classes.card}>
                <div className={classes.cardHead}>
                    <PauseCircleOutlineIcon style={{ fontSize: 17 }} />
                    Hold leads by hold revoke month
                    <span className={classes.cardNote}>
                        {loading ? "loading…" : "click any number to open the leads behind it"}
                    </span>
                </div>

                {loading && !loadedOnce ? (
                    <div style={{ padding: "0 20px 20px" }}>
                        {[0, 1, 2, 3, 4].map((i) => (
                            <Skeleton key={i} variant="rect" height={34} style={{ marginBottom: 8, borderRadius: 8 }} />
                        ))}
                    </div>
                ) : !store.length ? (
                    <div className={classes.emptyState}>
                        <InboxIcon style={{ fontSize: 44, color: "#d9cdc5" }} />
                        <div className={classes.emptyTitle}>No hold leads in this view</div>
                        <div className={classes.emptyHint}>
                            Try widening the hold revoke dates, or clearing the location and centre filters.
                        </div>
                    </div>
                ) : (
                    <div className={classes.pivotWrap}>
                        <Table size="small" stickyHeader style={{ minWidth: 600 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell className={`${classes.headCell} ${classes.headCellCorner}`}>
                                        Hold Revoke Month
                                    </TableCell>
                                    {/* Unchanged: the last row of `store` is the
                                        roll-up, which the Grand Total header
                                        below stands in for. */}
                                    {store.slice(0, uniqueexperienceCenterCode.length - 1).map((ec, index) => (
                                        <TableCell className={classes.headCell} key={index}>
                                            <Tooltip title={`${ec.experienceCenterName} — ${ec.expCtrCityName}`} placement="top">
                                                <span style={{ cursor: "help", borderBottom: "1px dotted rgba(255,255,255,0.5)" }}>
                                                    {ec.expCenterCode}
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    ))}
                                    <TableCell className={classes.headCell}>Grand Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {months.map((month, monthIndex) => (
                                    <TableRow key={monthIndex} hover>
                                        <TableCell className={`${classes.modalCell} ${classes.monthCell}`}>
                                            {`${month.monthName.slice(0, 3)}-${month.year % 100}`}
                                        </TableCell>
                                        {uniqueexperienceCenterNames.map((experienceCenterName, expCenterIndex) => (
                                            <React.Fragment key={experienceCenterName}>
                                                {expCenterData[experienceCenterName].map((innerEl, dataIndex) => {
                                                    const value = cellValue(innerEl, month);
                                                    const isTotalColumn = expCenterIndex === uniqueexperienceCenterNames.length - 1;
                                                    if (!value) {
                                                        return (
                                                            <TableCell key={dataIndex} className={classes.zeroCell}>0</TableCell>
                                                        );
                                                    }
                                                    return (
                                                        <TableCell
                                                            key={dataIndex}
                                                            className={classes.valueCell}
                                                            style={isTotalColumn ? { fontWeight: 800, color: "#1f5c3d" } : undefined}
                                                            onClick={(e) => getDataBasedOnClickHold(e, month, innerEl, "leads")}
                                                        >
                                                            {value.toLocaleString()}
                                                        </TableCell>
                                                    );
                                                })}
                                            </React.Fragment>
                                        ))}
                                    </TableRow>
                                ))}

                                <TableRow className={classes.totalRow}>
                                    <TableCell className={`${classes.modalCell} ${classes.monthCell} ${classes.totalCell}`} style={{ background: "#fbfaf9" }}>
                                        Grand Total
                                    </TableCell>
                                    {uniqueexperienceCenterNames.map((experienceCenterName, index) => {
                                        const total = store.reduce(
                                            (sum, el) => sum + (el.experienceCenterName === experienceCenterName ? el.count : 0),
                                            0,
                                        );
                                        if (!total) {
                                            return <TableCell key={index} className={classes.zeroCell}>0</TableCell>;
                                        }
                                        return (
                                            <TableCell
                                                key={index}
                                                className={`${classes.valueCell} ${classes.totalCell}`}
                                                onClick={(e) => getDataBasedOnClickHold(e, months, experienceCenterName, "total")}
                                            >
                                                {total.toLocaleString()}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            </TableBody>
                        </Table>
                    </div>
                )}
            </Paper>

            {/* ------------------------------------------------------- modal */}
            <Modal
                title={null}
                centered
                visible={showDetailedModal}
                onCancel={() => setShowDetailedModal(false)}
                // A NUMBER, not "94vw". antd writes `width` straight onto
                // .ant-modal, and a viewport-unit width fought the centring
                // rules, pulling the dialog left. A number keeps antd's own
                // `margin: 0 auto`, and maxWidth is what makes it shrink on a
                // narrow screen.
                width={1400}
                style={{ maxWidth: "94vw" }}
                maskClosable
                footer={null}
                closable={false}
                bodyStyle={{ padding: 0, borderRadius: 16, overflow: "hidden" }}
            >
                <div style={{ background: HEADER_GRADIENT, color: "#fff", padding: "16px 22px", display: "flex", alignItems: "center", gap: 12 }}>
                    <PauseCircleOutlineIcon style={{ fontSize: 22 }} />
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 16, fontWeight: 800 }}>Hold leads</div>
                        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.9)", marginTop: 2 }}>
                            The leads behind the number you clicked
                        </div>
                    </div>
                    <IconButton
                        size="small"
                        onClick={() => setShowDetailedModal(false)}
                        style={{ marginLeft: "auto", background: "rgba(0,0,0,0.22)", color: "#fff" }}
                    >
                        <CloseIcon style={{ fontSize: 18 }} />
                    </IconButton>
                </div>

                <div style={{ padding: "16px 22px 22px" }}>
                    <div className={classes.modalBar}>
                        <span className={classes.modalCount}>
                            <PlaceOutlinedIcon style={{ fontSize: 14 }} />
                            {detailLoading ? "Loading…" : `${popMonthData.length} lead${popMonthData.length === 1 ? "" : "s"}`}
                        </span>

                        <CSVLink headers={headers} filename={"hold_data.csv"} data={csvData} ref={csvButtonRef} />
                        <CSVLink headers={tableHeaders} filename={"hold_table_data.csv"} data={tableCsvData} ref={tableCsvButtonRef} />

                        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                            {!isMarketingAnalyst && (
                                <Button
                                    onClick={csvDownloadHandler}
                                    className={classes.exportBtn}
                                    loading={exporting === "full"}
                                    disabled={detailLoading || !popMonthData.length || Boolean(exporting)}
                                    icon={<GetAppIcon style={{ fontSize: 15, marginRight: 6 }} />}
                                >
                                    {exporting === "full" ? "Preparing…" : "Download full CSV"}
                                </Button>
                            )}
                            <Button
                                onClick={generateTableCsv}
                                className={classes.exportBtn}
                                loading={exporting === "table"}
                                disabled={detailLoading || !popMonthData.length || Boolean(exporting)}
                                icon={<GetAppIcon style={{ fontSize: 15, marginRight: 6 }} />}
                            >
                                {exporting === "table" ? "Preparing…" : "Export this table"}
                            </Button>
                        </div>
                    </div>

                    {detailLoading ? (
                        <div>
                            {[0, 1, 2, 3, 4, 5].map((i) => (
                                <Skeleton key={i} variant="rect" height={32} style={{ marginBottom: 7, borderRadius: 7 }} />
                            ))}
                        </div>
                    ) : !popMonthData.length ? (
                        <div className={classes.emptyState}>
                            <InboxIcon style={{ fontSize: 40, color: "#d9cdc5" }} />
                            <div className={classes.emptyTitle}>No leads in this cell</div>
                        </div>
                    ) : (
                        <div className={classes.modalTableWrap}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell className={classes.modalHeadCell}>Sl. No.</TableCell>
                                        <TableCell className={classes.modalHeadCell}>Project No.</TableCell>
                                        <TableCell className={classes.modalHeadCell}>Lead No.</TableCell>
                                        <TableCell className={classes.modalHeadCell}>Created At</TableCell>
                                        <TableCell className={classes.modalHeadCell}>Hold Revoke Date</TableCell>
                                        <TableCell className={classes.modalHeadCell}>Source of Lead</TableCell>
                                        <TableCell className={classes.modalHeadCell}>Client Name</TableCell>
                                        <TableCell className={classes.modalHeadCell}>Client Phone Number</TableCell>
                                        <TableCell className={classes.modalHeadCell}>Client Email</TableCell>
                                        <TableCell className={classes.modalHeadCell}>Assigned To</TableCell>
                                        <TableCell className={classes.modalHeadCell}>Address</TableCell>
                                        <TableCell className={classes.modalHeadCell}>City</TableCell>
                                        <TableCell className={classes.modalHeadCell}>Exp. Ctr. Name</TableCell>
                                        <TableCell className={classes.modalHeadCell}>Current Stage</TableCell>
                                        <TableCell className={classes.modalHeadCell}>Lead Type</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {popMonthData.map((data, i) => (
                                        <TableRow key={data?._id || i} className={classes.modalRow}>
                                            <TableCell className={classes.modalCell}>{i + 1}</TableCell>
                                            <TableCell className={classes.modalCell}>
                                                {data?.erpProjectNo ? (
                                                    <Link
                                                        to={`/project-logs/${data?._id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={classes.leadLink}
                                                    >
                                                        {data?.erpProjectNo}
                                                    </Link>
                                                ) : "NA"}
                                            </TableCell>
                                            <TableCell className={classes.modalCell}>
                                                {data?.lead_no ? (
                                                    <Link
                                                        to={`/project-logs/${data?._id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={classes.leadLink}
                                                    >
                                                        {data?.lead_no}
                                                    </Link>
                                                ) : "NA"}
                                            </TableCell>
                                            <TableCell className={classes.modalCell}>
                                                {data?.createdAt ? moment(data?.createdAt).format("DD-MM-YYYY") : "-"}
                                            </TableCell>
                                            <TableCell className={classes.modalCell}>
                                                {(data?.holdRevokeDate || data?.handoverDate)
                                                    ? moment(data?.holdRevokeDate || data?.handoverDate).format("DD-MM-YYYY") : "-"}
                                            </TableCell>
                                            <TableCell className={classes.modalCell}>{data?.sourceOfLead?.name || "-"}</TableCell>
                                            <TableCell className={classes.modalCell}>{data?.customerId?.name || "-"}</TableCell>
                                            <TableCell className={classes.modalCell}>{data?.customerId?.contact_no || "-"}</TableCell>
                                            <TableCell className={classes.modalCell}>{data?.customerId?.email || "-"}</TableCell>
                                            <TableCell className={classes.modalCell}>{data?.assignTo?.name || "-"}</TableCell>
                                            <TableCell className={classes.modalCell} style={{ whiteSpace: "normal", minWidth: 180 }}>
                                                {data?.customerId?.address || "-"}
                                            </TableCell>
                                            <TableCell className={classes.modalCell}>{data?.experienceCenterId?.locationId?.name || "-"}</TableCell>
                                            <TableCell className={classes.modalCell}>{data?.experienceCenterId?.name || "-"}</TableCell>
                                            <TableCell className={classes.modalCell}>{data?.currentStage ? data.currentStage : "NA"}</TableCell>
                                            <TableCell className={classes.modalCell}>{data?.leadType ? data.leadType : "NA"}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default connect(null, { setSnackInfo })(HoldDashboard);
